import {
  __ONLY_WRITE_STATE_FROM_ACTIONS as writeState,
  isPersistent,
  isVolatile,
  readState,
} from "@/__main__/app-state.mjs";
import { MAX_TIME } from "@/__main__/constants.mjs";
import getData from "@/__main__/get-data.mjs";
import { hardCodeURLs } from "@/app/constants.mjs";
import type {
  GreenhouseDepartments,
  GreenhouseJobs,
} from "@/data-models/greenhouse-model.mjs";
import {
  DepartmentsModel,
  JobsModel,
} from "@/data-models/greenhouse-model.mjs";
import clone from "@/util/clone.mjs";

// Dismiss a snackbar message for at least ten minutes
const TRANSIENT_DISMISS_TIME = 10 * 60 * 1000;

/**
 * If a marketing snackbar explicitly sets `dismissable` to `true`, it is fully
 * dismissable and won't reappear.
 */
export function addDismissedSnackbar(
  id: string,
  { permanent = false }: { permanent?: boolean } = {},
): Promise<void> {
  if (permanent) {
    const dismissed: string[] = clone(readState.marketing.dismissed);

    if (!dismissed.includes(id)) {
      dismissed.push(id);
    } else {
      return;
    }

    dismissed[isPersistent] = MAX_TIME;

    writeState.marketing.dismissed = dismissed;
  } else {
    const dismissed = clone(readState.volatile.marketing?.dismissed ?? {});

    dismissed[id] = Date.now() + TRANSIENT_DISMISS_TIME;

    dismissed[isVolatile] = true;

    writeState.volatile.marketing.dismissed = dismissed;
  }
}

export async function fetchJobs() {
  await getData(
    hardCodeURLs.GREENHOUSE_JOBS,
    JobsModel,
    ["marketing", "jobs"],
    {
      shouldSkipDBRead: true,
      networkBackOffTime: 24 * 60 * 60 * 1000, // 24 hours
    },
  );
}

export async function fetchDepartments() {
  await getData(
    hardCodeURLs.GREENHOUSE_DEPARTMENTS,
    DepartmentsModel,
    ["marketing", "departments"],
    {
      shouldSkipDBRead: true,
      networkBackOffTime: 24 * 60 * 60 * 1000, // 24 hours
    },
  );
}

declare module "@/__main__/app-state.mjs" {
  interface AppState {
    marketing: {
      dismissed: string[];
      newNavItems: Record<string, boolean>;

      jobs: GreenhouseJobs;
      departments: GreenhouseDepartments;
      previousMatchTileAd: Record<string, string>;
      referrerInfo: Record<string, string>;
      seenPromoFeatures: Record<string, boolean>;
    };
  }

  interface VolatileState {
    marketing?: {
      // If a marketing snackbar is given an `id` and `dismissable` is unset, it
      // can be temporarily dismissed but may reappear after a page load, or a
      // short amount of time
      dismissed?: { [key: string]: number };
    };
  }
}
